import io from "socket.io-client";
import api from "../services/api";

let activeWorker = null;

const normalizeIdentityPart = value => String(value ?? "").trim();

const readStoredAccessToken = () => {
  try {
    const stored = sessionStorage.getItem("token") || localStorage.getItem("token");
    if (!stored) return "";
    let token = stored;
    try {
      token = JSON.parse(stored);
    } catch (_error) {}
    return normalizeIdentityPart(token).replace(/^Bearer\s+/i, "");
  } catch (_error) {
    return "";
  }
};

const getCurrentSessionToken = () => {
  const storedToken = readStoredAccessToken();
  if (storedToken) return `Bearer ${storedToken}`;
  return normalizeIdentityPart(api.defaults.headers.Authorization);
};

class SocketWorker {
  constructor(companyId, userId, token, onManualDisconnect) {
    this.companyId = normalizeIdentityPart(companyId);
    this.userId = normalizeIdentityPart(userId);
    this.token = normalizeIdentityPart(token);
    this.socket = null;
    this.eventListeners = {};
    this.manuallyDisconnected = false;
    this.onManualDisconnect = onManualDisconnect;
    this.handleAccessTokenRefresh = event => {
      const refreshedToken = normalizeIdentityPart(event?.detail?.token);
      if (!refreshedToken) return;
      this.refreshAuthentication(refreshedToken);
      if (!this.connected) this.connect();
    };
    if (typeof window !== "undefined") {
      window.addEventListener(
        "estratiza-auth-token-refreshed",
        this.handleAccessTokenRefresh
      );
    }
    this.configureSocket();
  }

  matches(companyId, userId) {
    return (
      this.companyId === normalizeIdentityPart(companyId) &&
      this.userId === normalizeIdentityPart(userId)
    );
  }

  refreshAuthentication(token = getCurrentSessionToken()) {
    const normalizedToken = normalizeIdentityPart(token);
    if (!normalizedToken) return;

    this.token = normalizedToken;
    if (!this.socket) return;

    // ITEM37-REALTIME-RECOVERY: o access token expira a cada 15 minutos.
    // Toda reconexao precisa usar o token atual sem perder os listeners.
    this.socket.auth = { userId: this.userId, token: normalizedToken };
    if (this.socket.io?.opts) {
      this.socket.io.opts.query = { userId: this.userId, token: normalizedToken };
    }
  }

  configureSocket() {
    if (!this.companyId || !this.userId || !this.token) return;

    this.manuallyDisconnected = false;
    this.socket = io(`${process.env.REACT_APP_BACKEND_URL}/${this.companyId}`, {
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: Infinity,
      query: { userId: this.userId, token: this.token },
      auth: { userId: this.userId, token: this.token }
    });

    this.handleReconnectAttempt = () => this.refreshAuthentication();
    if (this.socket.io?.on) {
      this.socket.io.on("reconnect_attempt", this.handleReconnectAttempt);
    }

    this.socket.on("connect", () => {
      console.log("Conectado ao servidor Socket.IO", this.companyId);
    });

    this.socket.on("disconnect", reason => {
      if (!this.manuallyDisconnected) {
        console.log("Desconectado do servidor Socket.IO", this.companyId);
        if (reason === "io server disconnect") {
          clearTimeout(this.serverReconnectTimer);
          this.serverReconnectTimer = setTimeout(() => {
            void this.recoverConnection();
          }, 1000);
        }
      }
    });

    this.socket.on("connect_error", () => {
      if (!this.manuallyDisconnected) {
        clearTimeout(this.serverReconnectTimer);
        this.serverReconnectTimer = setTimeout(() => {
          void this.recoverConnection();
        }, 1000);
      }
    });
  }

  async recoverConnection() {
    if (this.manuallyDisconnected) return;
    try {
      // Forca o interceptor HTTP a renovar o access token, caso necessario,
      // antes de repetir o handshake autenticado do Socket.IO.
      await api.get("/auth/me");
    } catch (_error) {}
    if (this.manuallyDisconnected) return;
    this.refreshAuthentication();
    this.connect();
  }

  on(event, callback) {
    this.connect();
    if (!this.socket) return;
    this.socket.on(event, callback);

    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    this.eventListeners[event].push(callback);
  }

  emit(event, data) {
    this.connect();
    if (this.socket) this.socket.emit(event, data);
  }

  off(event, callback) {
    if (!this.socket || !this.eventListeners[event]) return;

    if (callback) {
      this.socket.off(event, callback);
      this.eventListeners[event] = this.eventListeners[event].filter(
        registered => registered !== callback
      );
      if (this.eventListeners[event].length === 0) {
        delete this.eventListeners[event];
      }
      return;
    }

    this.eventListeners[event].forEach(registered =>
      this.socket.off(event, registered)
    );
    delete this.eventListeners[event];
  }

  disconnect() {
    this.manuallyDisconnected = true;
    clearTimeout(this.serverReconnectTimer);
    if (typeof window !== "undefined" && this.handleAccessTokenRefresh) {
      window.removeEventListener(
        "estratiza-auth-token-refreshed",
        this.handleAccessTokenRefresh
      );
    }
    const currentSocket = this.socket;
    this.socket = null;
    this.eventListeners = {};

    if (currentSocket) {
      if (currentSocket.io?.off && this.handleReconnectAttempt) {
        currentSocket.io.off("reconnect_attempt", this.handleReconnectAttempt);
      }
      currentSocket.removeAllListeners();
      currentSocket.disconnect();
    }
    if (this.onManualDisconnect) this.onManualDisconnect(this);
    console.log("Socket desconectado manualmente", this.companyId);
  }

  get connected() {
    return !!this.socket?.connected;
  }

  connect() {
    if (this.manuallyDisconnected) return;
    if (!this.socket) {
      this.configureSocket();
    } else if (!this.socket.connected && typeof this.socket.connect === "function") {
      this.refreshAuthentication();
      this.socket.connect();
    }
  }

  forceReconnect() {
    if (!this.socket) {
      this.manuallyDisconnected = false;
      this.configureSocket();
      return;
    }
    this.refreshAuthentication();
    this.socket.disconnect();
    this.socket.connect();
  }
}

const instance = (companyId, userId) => {
  const sessionToken = getCurrentSessionToken();

  if (!activeWorker?.matches(companyId, userId)) {
    if (activeWorker) activeWorker.disconnect();
    activeWorker = new SocketWorker(
      companyId,
      userId,
      sessionToken,
      worker => {
        if (activeWorker === worker) activeWorker = null;
      }
    );
  } else {
    activeWorker.refreshAuthentication(sessionToken);
  }
  return activeWorker;
};

export default instance;
